import { PaginatedQueryResult } from '@query/usePaginatedQuery'
import classNames from 'classnames'
import { FC, ReactNode, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@context/AuthContext'
import { useIsRTL } from '@context/SessionSettingsContext'

import { Body1 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { ListItem } from '@ui/components/List'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { ArrowsDownUpIcon } from '@ui/icons/ArrowsDownUp.icon'
import { EditIcon } from '@ui/icons/Edit.icon'
import { KebabIcon } from '@ui/icons/Kebab.icon'
import {
  SensitiveTableCell,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadSort,
  TableHeader,
  TableNoResults,
  TableRow,
} from '@ui/v2/Table/Table'
import { TableHeaderTooltip } from '@ui/v2/Table/TableHeaderTooltip'

import { formatDate } from '@utils/date.utils'
import { useWindowResize } from '@utils/domUtils'
import { placeholder } from '@utils/placeholder'

import { NameDto } from '@model/NameDto'
import {
  TradingAccountRebateDetailedDto,
  hasRebateSettings,
} from '@model/TradingAccountRebateDetailedDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import { TradingAccountsRebateSortOptions } from './hooks/useIBTradingAccountsRebate'

import styles from './ClientsPageTable.module.scss'

interface ClientsPageTableProps {
  sortOptions?: NameDto<TradingAccountsRebateSortOptions | null>[]
  sticky?: 'left'
  selectedIds?: string[]
  onSelect?: (ids: string[]) => void
  onEdit: (client: TradingAccountRebateDetailedDto) => void
  onReset: (client: TradingAccountRebateDetailedDto) => void
  tableQuery: PaginatedQueryResult<Partial<TradingAccountRebateDetailedDto>>
  isAnyFilterActive: boolean
  topContent?: ReactNode
  isRebatesManagement?: boolean
  handleAddToGroupClick?: () => void
  handleEditRebate?: () => void
}

export const ClientsPageTable: FC<ClientsPageTableProps> = (props) => {
  const {
    sortOptions,
    sticky,
    selectedIds = [],
    onEdit,
    isAnyFilterActive,
    onSelect,
    onReset,
    tableQuery,
    topContent,
    isRebatesManagement,
  } = props

  const { isLoading, setPageQuery, pageQuery, data: tableData } = tableQuery
  const isMobile = useWindowResize()
  const { formatMoney } = useFormatNumber()
  const { t } = useTranslation()
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name
  const isRTL = useIsRTL()
  const handleSelect = (id: string) => {
    if (!onSelect) return

    const newSelection = new Set(selectedIds)

    if (newSelection.has(id)) {
      newSelection.delete(id)
    } else {
      newSelection.add(id)
    }

    onSelect(Array.from(newSelection))
  }

  const selectableIds = tableData.map((item) => item.tradingAccount?.id).filter(Boolean) as string[]

  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id))
  const someSelected = !allSelected && selectableIds.some((id) => selectedIds.includes(id))

  const handleSelectAll = () => {
    if (!onSelect) return
    onSelect(allSelected ? [] : selectableIds)
  }
  const getColumnAlign = (id?: TradingAccountsRebateSortOptions) => {
    switch (id) {
      case 'RebateGroupId':
      case 'TotalVolumeFxm':
      case 'TotalCommission':
      case 'RebateAmountInPercentage':
      case 'TotalVolumeOther':
        return isRTL ? 'left' : 'right'

      default:
        return isRTL ? 'right' : 'left'
    }
  }
  return (
    <Table
      isLoading={isLoading}
      noResults={
        !tableQuery.data.length && <EmptyClientsPageTable isAnyFilterActive={isAnyFilterActive} />
      }
      sticky={sticky}
      className={classNames(styles.table, {
        [styles.compact]: isRebatesManagement,
      })}
      topContent={topContent}
      size={isRebatesManagement ? 'sm' : 'md'}
      hideScrollbarOnMobile
    >
      <TableHeader selectedIsActive={selectedIds.length > 0}>
        <TableRow>
          {sortOptions?.map((option) => {
            return option.id ? (
              <TableHeadSort
                id={option.id}
                key={option.id}
                onSelectAll={
                  option.id === 'TradingAccountName' && onSelect ? handleSelectAll : undefined
                }
                allSelected={option.id === 'TradingAccountName' ? allSelected : undefined}
                someSelected={option.id === 'TradingAccountName' ? someSelected : undefined}
                sort={pageQuery?.sort || undefined}
                sortOrder={pageQuery?.sortOrder}
                selectDisabled={!selectableIds.length}
                setSort={(sort, sortOrder) => {
                  setPageQuery?.({
                    ...pageQuery,
                    sort,
                    sortOrder,
                  })
                }}
                align={getColumnAlign(option.id)}
              >
                {option.name}
                <TableHeaderTooltip<Partial<Record<TradingAccountsRebateSortOptions, string>>>
                  headerIds={{
                    TotalVolumeFxm: t('IB.RebatesPage.Forex & Metals'),
                    TotalVolumeOther: t('IB.RebatesPage.Other instrument groups'),
                    TotalCommission: t('IB.RebatesPage.Commission accrued for TA'),
                  }}
                  targetHeaderId={option.id}
                />
              </TableHeadSort>
            ) : (
              <TableHead key={option.name}>{option.name}</TableHead>
            )
          })}
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {tableData.map((item) => {
          const selected = selectedIds?.includes(item.tradingAccount?.id as string)

          return (
            <TableRow key={item.tradingAccount?.id} selected={selected}>
              {item.tradingAccount ? (
                <SensitiveTableCell
                  selected={selected}
                  onSelect={() => handleSelect(item.tradingAccount ? item.tradingAccount.id : '')}
                  align='left'
                >
                  {item.tradingAccount?.name}
                </SensitiveTableCell>
              ) : null}
              {item.client ? (
                <SensitiveTableCell align='left'>{item.client?.name}</SensitiveTableCell>
              ) : null}
              {item.tradingAccountType ? (
                <SensitiveTableCell align='left'>
                  {item.tradingAccountType?.name}
                </SensitiveTableCell>
              ) : null}
              {item.tradingAccountSignedUpDate ? (
                <SensitiveTableCell align='left'>
                  {formatDate(item.tradingAccountSignedUpDate, {
                    formatType: dateFormat,
                    withTime: false,
                  })}
                </SensitiveTableCell>
              ) : null}
              {item.country ? (
                <SensitiveTableCell align='left'>{item.country?.name}</SensitiveTableCell>
              ) : null}
              <SensitiveTableCell align='left'>
                {placeholder.NA(t, item.rebateApprovalType?.name)}
              </SensitiveTableCell>
              <SensitiveTableCell align='left'>
                {placeholder.NA(t, item.rebatePeriod?.name)}
              </SensitiveTableCell>
              {sortOptions?.some((option) => option.id === 'RebateGroupName') ? (
                <SensitiveTableCell align='left'>
                  {placeholder.NA(t, item.rebateGroup?.name)}
                </SensitiveTableCell>
              ) : null}
              {typeof item.totalVolumeFxm === 'number' ? (
                <SensitiveTableCell align='right'>
                  <Tooltip
                    content={formatMoney(item.totalVolumeFxm)}
                    disabled={item.totalVolumeFxm < 100_000_000}
                  >
                    {formatMoney(item.totalVolumeFxm)}
                  </Tooltip>
                </SensitiveTableCell>
              ) : null}
              {typeof item.totalVolumeOther === 'number' ? (
                <SensitiveTableCell align='right'>
                  <Tooltip
                    content={formatMoney(item.totalVolumeOther)}
                    disabled={item.totalVolumeOther < 100_000_000}
                  >
                    {formatMoney(item.totalVolumeOther)}
                  </Tooltip>
                </SensitiveTableCell>
              ) : null}
              {typeof item.totalCommission === 'number' ? (
                <SensitiveTableCell align='right'>
                  {formatMoney(item.totalCommission)}
                </SensitiveTableCell>
              ) : null}
              <SensitiveTableCell align='right'>
                {!item.rebateAmountInPercentage ? t('N/A') : `${item.rebateAmountInPercentage}%`}
              </SensitiveTableCell>
              <TableCell
                hasTruncate={false}
                align='right'
                className={styles.rowActions}
                width={isRebatesManagement && !isMobile ? '' : 50}
              >
                {isRebatesManagement && !isMobile ? (
                  <Box flex gap='x8'>
                    <IconButton.Secondary
                      size='sm'
                      icon={EditIcon}
                      onClick={() => onEdit(item as TradingAccountRebateDetailedDto)}
                    />

                    <IconButton.Secondary
                      size='sm'
                      icon={ArrowsDownUpIcon}
                      onClick={() => onReset(item as TradingAccountRebateDetailedDto)}
                    />
                  </Box>
                ) : (
                  <RowActions
                    client={item as TradingAccountRebateDetailedDto}
                    onEdit={() => onEdit(item as TradingAccountRebateDetailedDto)}
                    onReset={() => onReset(item as TradingAccountRebateDetailedDto)}
                  />
                )}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

interface RowActionsProps {
  onEdit: () => void
  onReset: () => void
  client: TradingAccountRebateDetailedDto
}

export const RowActions: FC<RowActionsProps> = (props) => {
  const { client, onEdit, onReset } = props

  const { t } = useTranslation()

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const actions = [
    {
      label: t('Edit'),
      onClick: () => {
        onEdit()
        setIsDropdownOpen(false)
      },
      icon: EditIcon,
    },
  ]

  if (hasRebateSettings(client)) {
    actions.push({
      label: t('Reset'),
      onClick: () => {
        onReset()
        setIsDropdownOpen(false)
      },
      icon: ArrowsDownUpIcon,
    })
  }

  return (
    <Popover
      direction='topRight'
      isOpen={isDropdownOpen}
      setIsOpen={setIsDropdownOpen}
      content={
        <PopoverCard>
          <PopoverList>
            {actions?.map((action) => (
              <ListItem
                key={action.label}
                onClick={action.onClick}
                startSlot={{ icon: action.icon }}
              >
                {action.label}
              </ListItem>
            ))}
          </PopoverList>
        </PopoverCard>
      }
    >
      <IconButton.Ghost icon={KebabIcon} />
    </Popover>
  )
}

interface EmptyClientsPageTableProps {
  isAnyFilterActive?: boolean
}

export const EmptyClientsPageTable: FC<EmptyClientsPageTableProps> = (props) => {
  const { isAnyFilterActive } = props

  const { t } = useTranslation()

  return (
    <TableNoResults
      title={
        isAnyFilterActive
          ? t('IB.RebatesPage.Nothing to show')
          : t('IB.RebatesPage.ClientsPage.No clients to display')
      }
      content={
        isAnyFilterActive ? <Body1>{t('IB.RebatesPage.Please, adjust your filters')}</Body1> : null
      }
    />
  )
}
